"use client";

import React from "react";
import { useLocalization } from "../../lib/translation";
import { VisuallyHidden } from "../VisuallyHidden";
import styles from "./Timestamp.module.css";

/**
 * Display format, mirroring the Astryx Timestamp reference
 * (https://astryx.atmeta.com/components/Timestamp): user-facing `date`,
 * `date_time`, `time` and `relative`; ISO-style `system_*` variants for
 * developer surfaces; `auto` switches from relative to date_time once the
 * value is older than `autoThreshold`.
 */
export type TimestampFormat =
  | "auto"
  | "relative"
  | "date"
  | "date_time"
  | "time"
  | "system_date"
  | "system_date_time"
  | "system_time";

export type TimestampSize = "xxs" | "xs" | "s" | "m";

export interface TimestampProps {
  /** The date/time to display: ISO 8601 calendar date or instant, Unix seconds, or Date. */
  value: string | number | Date;
  /** Display format. @default "auto" */
  format?: TimestampFormat;
  /**
   * Seconds after which `auto` switches from relative to date_time.
   * @default 604800 (7 days)
   */
  autoThreshold?: number;
  /** Text-ladder size. @default "s" */
  size?: TimestampSize;
  /** Color role: body text or muted metadata. @default "muted" */
  tone?: "default" | "muted";
  /** Append the timezone abbreviation (date_time/time/system equivalents). @default false */
  showTimezone?: boolean;
  /**
   * Full date for relative output: a native tooltip (title) for pointer users
   * plus the same text, visually hidden, inside the <time> for screen readers.
   * @default true
   */
  tooltip?: boolean;
  /** Keep relative output ticking while mounted. @default false */
  live?: boolean;
  /**
   * Reference "now" for relative/auto output. Defaults to render time; pass a
   * fixed value in stories/tests for deterministic output.
   */
  now?: string | number | Date;
  /** Locale override; defaults to the active site language. */
  locale?: string;
  /**
   * IANA time zone for the shown date and time, for example "Europe/Helsinki".
   * Defaults to the runtime's zone, which is UTC on a server render: set it
   * when the moment must read the same everywhere (a business's local time).
   * Ignored for a date-only (YYYY-MM-DD) value, which has no time of day and
   * always shows its own calendar date.
   */
  timeZone?: string;
  /** Optional className passthrough. */
  className?: string;
}

const SIZE_CLASS: Record<TimestampSize, string> = {
  xxs: styles.sizeXXS,
  xs: styles.sizeXS,
  s: styles.sizeS,
  m: styles.sizeM,
};

type ParsedTimestamp = {
  date: Date;
  dateTime: string | undefined;
  /** True for a YYYY-MM-DD calendar date (no time of day, no zone). */
  dateOnly?: boolean;
};

const ISO_CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MAX_FOUR_DIGIT_UNIX_SECONDS = 253_402_300_799;

const invalidTimestamp = (): ParsedTimestamp => ({
  date: new Date(Number.NaN),
  dateTime: undefined,
});

const parseTimestampValue = (
  value: string | number | Date,
): ParsedTimestamp => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? invalidTimestamp()
      : { date: value, dateTime: value.toISOString() };
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return invalidTimestamp();

    const epochMs =
      Math.abs(value) <= MAX_FOUR_DIGIT_UNIX_SECONDS ? value * 1000 : value;
    const date = new Date(epochMs);
    return Number.isNaN(date.getTime())
      ? invalidTimestamp()
      : { date, dateTime: date.toISOString() };
  }

  const normalizedValue = value.trim();
  if (!normalizedValue) return invalidTimestamp();

  const calendarDate = ISO_CALENDAR_DATE.exec(normalizedValue);
  if (calendarDate) {
    const year = Number(calendarDate[1]);
    const month = Number(calendarDate[2]) - 1;
    const day = Number(calendarDate[3]);
    const date = new Date(2000, month, day);
    date.setFullYear(year);

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month ||
      date.getDate() !== day
    ) {
      return invalidTimestamp();
    }

    return { date, dateTime: normalizedValue, dateOnly: true };
  }

  const date = new Date(normalizedValue);
  return Number.isNaN(date.getTime())
    ? invalidTimestamp()
    : { date, dateTime: date.toISOString() };
};

const pad = (n: number) => String(n).padStart(2, "0");

/** Calendar fields in `timeZone`, or in the runtime's zone without one. */
const zonedParts = (d: Date, timeZone?: string) => {
  if (!timeZone)
    return {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
      hour: d.getHours(),
      minute: d.getMinutes(),
      second: d.getSeconds(),
    };
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  }).formatToParts(d);
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
};

const systemDate = (d: Date, timeZone?: string) => {
  const p = zonedParts(d, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
};
const systemTime = (d: Date, timeZone?: string) => {
  const p = zonedParts(d, timeZone);
  return `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`;
};

const timezoneAbbreviation = (d: Date, locale: string, timeZone?: string) => {
  const part = new Intl.DateTimeFormat(locale, {
    timeZoneName: "short",
    timeZone,
  })
    .formatToParts(d)
    .find((p) => p.type === "timeZoneName");
  return part ? part.value : "";
};

const RELATIVE_STEPS: Array<{
  limit: number;
  divisor: number;
  unit: Intl.RelativeTimeFormatUnit;
}> = [
  { limit: 60, divisor: 1, unit: "second" },
  { limit: 3600, divisor: 60, unit: "minute" },
  { limit: 86400, divisor: 3600, unit: "hour" },
  { limit: 604800, divisor: 86400, unit: "day" },
  { limit: 2629800, divisor: 604800, unit: "week" },
  { limit: 31557600, divisor: 2629800, unit: "month" },
  { limit: Infinity, divisor: 31557600, unit: "year" },
];

const relativeLabel = (date: Date, nowDate: Date, locale: string) => {
  const deltaSeconds = Math.round((date.getTime() - nowDate.getTime()) / 1000);
  const magnitude = Math.abs(deltaSeconds);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (magnitude < 45) return rtf.format(0, "second"); // "now"
  const step =
    RELATIVE_STEPS.find((s) => magnitude < s.limit) ??
    RELATIVE_STEPS[RELATIVE_STEPS.length - 1];
  return rtf.format(Math.round(deltaSeconds / step.divisor), step.unit);
};

/** Pure formatter, exported for tests. */
export const formatTimestamp = (
  date: Date,
  nowDate: Date,
  format: TimestampFormat,
  locale: string,
  showTimezone: boolean,
  autoThreshold: number,
  timeZone?: string,
): string => {
  if (Number.isNaN(date.getTime())) return "";
  if (
    (format === "auto" || format === "relative") &&
    Number.isNaN(nowDate.getTime())
  ) {
    return "";
  }

  const resolved: Exclude<TimestampFormat, "auto"> =
    format === "auto"
      ? Math.abs(nowDate.getTime() - date.getTime()) / 1000 < autoThreshold
        ? "relative"
        : "date_time"
      : format;

  const tz = showTimezone
    ? ` ${timezoneAbbreviation(date, locale, timeZone)}`
    : "";

  switch (resolved) {
    case "relative":
      return relativeLabel(date, nowDate, locale);
    case "date":
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone,
      }).format(date);
    case "date_time":
      return (
        new Intl.DateTimeFormat(locale, {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZone,
        }).format(date) + tz
      );
    case "time":
      return (
        new Intl.DateTimeFormat(locale, {
          hour: "2-digit",
          minute: "2-digit",
          timeZone,
        }).format(date) + tz
      );
    case "system_date":
      return systemDate(date, timeZone);
    case "system_date_time":
      return `${systemDate(date, timeZone)} ${systemTime(date, timeZone)}${tz}`;
    case "system_time":
      return `${systemTime(date, timeZone)}${tz}`;
  }
};

/**
 * Formats a date or time value into human-readable text: relative for
 * recency, absolute for precision, or `auto` to switch between them.
 * Renders a semantic `<time>` with a machine-readable dateTime attribute;
 * relative modes carry the full date as a native tooltip.
 */
export const Timestamp = React.forwardRef<HTMLTimeElement, TimestampProps>(
  function Timestamp(
    {
      value,
      format = "auto",
      autoThreshold = 604800,
      size = "s",
      tone = "muted",
      showTimezone = false,
      tooltip = true,
      live = false,
      now,
      locale,
      timeZone,
      className = "",
    },
    ref,
  ) {
    const { resolvedLanguage } = useLocalization();
    const activeLocale = locale || resolvedLanguage || "en";
    const parsedValue = parseTimestampValue(value);
    const date = parsedValue.date;
    // An empty string (an unset Storybook text control) means "no zone", not an invalid one.
    // A date-only value is built as local midnight, so converting it to another
    // zone would shift the calendar day; it is always shown in the runtime zone.
    const zone = parsedValue.dateOnly ? undefined : timeZone || undefined;

    // Ticks only when live; otherwise "now" is captured per render pass.
    const [tick, setTick] = React.useState(0);
    const nowDate = React.useMemo(
      () => (now !== undefined ? parseTimestampValue(now).date : new Date()),
      // eslint-disable-next-line react-hooks/exhaustive-deps -- tick forces live re-evaluation
      [now, tick],
    );

    React.useEffect(() => {
      if (!live || now !== undefined) return;
      if (format !== "relative" && format !== "auto") return;
      const interval = window.setInterval(() => setTick((v) => v + 1), 30_000);
      return () => window.clearInterval(interval);
    }, [live, now, format]);

    const valueIsValid = !Number.isNaN(date.getTime());
    const nowIsValid = !Number.isNaN(nowDate.getTime());
    const isValid =
      valueIsValid &&
      (format !== "relative" && format !== "auto" ? true : nowIsValid);
    const label = isValid
      ? formatTimestamp(
          date,
          nowDate,
          format,
          activeLocale,
          showTimezone,
          autoThreshold,
          zone,
        )
      : "";

    const showsRelative =
      format === "relative" ||
      (format === "auto" &&
        isValid &&
        Math.abs(nowDate.getTime() - date.getTime()) / 1000 < autoThreshold);

    const fullDate =
      isValid && tooltip && showsRelative
        ? new Intl.DateTimeFormat(
            activeLocale,
            parsedValue.dateOnly
              ? { dateStyle: "medium" }
              : { dateStyle: "medium", timeStyle: "short", timeZone: zone },
          ).format(date)
        : undefined;

    return (
      <time
        ref={ref}
        dateTime={isValid ? parsedValue.dateTime : undefined}
        title={fullDate}
        className={`${styles.timestamp} ${SIZE_CLASS[size]} ${
          tone === "muted" ? styles.toneMuted : styles.toneDefault
        } ${className}`.trim()}
        // Relative output derives from render-time "now"; server and client
        // can legitimately differ by a tick (React-sanctioned use of
        // suppressHydrationWarning for timestamps).
        suppressHydrationWarning
      >
        {label}
        {/* title reaches pointer users only; screen readers get the full
            date as text inside the <time>. */}
        {fullDate && <VisuallyHidden> ({fullDate})</VisuallyHidden>}
      </time>
    );
  },
);

export default Timestamp;
